import { useMemo, useCallback, useEffect } from 'react';
import sortBy from 'lodash/sortBy';

import type { GetDraftLessonActivitiesQuery } from '../lesson-builder.graphql.generated';
import { getSectionsAndSubSectionsOverview } from '../activity-builder.utils';
import { ExtraSectionIdentifiers } from '../activity-builder.apollo';
import { ARROW_LEFT, ARROW_RIGHT } from 'approot/shared/ally/keyboard.hooks';
import { SectionOrSubSectionOverview } from '../activity-builder.constants';
import { StudentSectionOrSubSectionOverview } from '../student/student.constants';
import { UpdateActiveSectionProps } from './activity-builder-present.constants';
import {
  ACTIVITY_BUILDER_EVENT_SECTION_VIEW,
  clearPresentationSessionId,
  setPresentationSessionId,
} from './activity-builder-present-data-tracking.utils';
import { pushDataTrackerPageView } from 'approot/shared/data-tracker/data-tracker-push-pageview';
import { checkAnswerVar } from 'approot/activity-builder/check-answer/check-answer.apollo';
import type { Pluck } from 'lib/gql/pluck';

export const usePresentOverviewData = (
  sectionsOverview:
    | Pluck<
        GetDraftLessonActivitiesQuery,
        'getDraftLessonActivities.sectionsOverview'
      >[]
    | undefined,
  activeSectionId: string
) => {
  const sortedSectionsOverview = useMemo(
    () => sortBy(sectionsOverview, 'sort'),
    [sectionsOverview]
  );
  const sortedSectionsAndSubSectionsOverview = useMemo(
    () => getSectionsAndSubSectionsOverview(sortedSectionsOverview),
    [sortedSectionsOverview]
  );
  const currentSectionOverviewIndex = useMemo(() => {
    return sortedSectionsAndSubSectionsOverview.findIndex(
      s => s.id === activeSectionId
    );
  }, [activeSectionId, sortedSectionsAndSubSectionsOverview]);
  const currentSectionOverview =
    sortedSectionsAndSubSectionsOverview[currentSectionOverviewIndex];

  return {
    sortedSectionsAndSubSectionsOverview,
    currentSectionOverviewIndex,
    currentSectionOverview,
  };
};

export const usePresentNavigation = ({
  activeSectionId,
  currentSectionOverviewIndex,
  sortedSectionsAndSubSectionsOverview,
  updateActiveSection,
  isEndOfLesson,
  hasIntroduction = true,
}: {
  activeSectionId: string;
  currentSectionOverviewIndex: number;
  sortedSectionsAndSubSectionsOverview: (
    | SectionOrSubSectionOverview
    | StudentSectionOrSubSectionOverview
  )[];
  updateActiveSection: ({
    sectionId,
    newSectionOverview,
    index,
    eventType,
  }: UpdateActiveSectionProps) => void;
  isEndOfLesson: boolean;
  hasIntroduction?: boolean;
}) => {
  const handlePrevious = useCallback(() => {
    if (activeSectionId === ExtraSectionIdentifiers.Conclusion) {
      updateActiveSection({
        sectionId: sortedSectionsAndSubSectionsOverview.length
          ? sortedSectionsAndSubSectionsOverview[
              sortedSectionsAndSubSectionsOverview.length - 1
            ].id
          : ExtraSectionIdentifiers.Introduction,
        newSectionOverview: sortedSectionsAndSubSectionsOverview.length
          ? (sortedSectionsAndSubSectionsOverview[
              sortedSectionsAndSubSectionsOverview.length - 1
            ] as SectionOrSubSectionOverview)
          : null,
        index: sortedSectionsAndSubSectionsOverview.length || 0,
        eventType: ACTIVITY_BUILDER_EVENT_SECTION_VIEW,
      });
    } else if (activeSectionId !== ExtraSectionIdentifiers.Introduction) {
      if (currentSectionOverviewIndex === 0 && hasIntroduction) {
        updateActiveSection({
          sectionId: ExtraSectionIdentifiers.Introduction,
          newSectionOverview: null,
          index: 0,
          eventType: ACTIVITY_BUILDER_EVENT_SECTION_VIEW,
        });
      } else if (currentSectionOverviewIndex > 0) {
        const previousSection =
          sortedSectionsAndSubSectionsOverview[currentSectionOverviewIndex - 1];
        updateActiveSection({
          sectionId: previousSection.id,
          newSectionOverview: previousSection as SectionOrSubSectionOverview,
          index: currentSectionOverviewIndex,
          eventType: ACTIVITY_BUILDER_EVENT_SECTION_VIEW,
        });
      }
    }
  }, [
    activeSectionId,
    updateActiveSection,
    sortedSectionsAndSubSectionsOverview,
    currentSectionOverviewIndex,
    hasIntroduction,
  ]);

  const handleNext = useCallback(() => {
    if (!isEndOfLesson) {
      const isNextSectionConclusion =
        currentSectionOverviewIndex ===
        sortedSectionsAndSubSectionsOverview.length - 1;
      const nextSectionId = isNextSectionConclusion
        ? ExtraSectionIdentifiers.Conclusion
        : sortedSectionsAndSubSectionsOverview[currentSectionOverviewIndex + 1]
            .id;

      updateActiveSection({
        sectionId: nextSectionId,
        newSectionOverview: !isNextSectionConclusion
          ? (sortedSectionsAndSubSectionsOverview[
              currentSectionOverviewIndex + 1
            ] as SectionOrSubSectionOverview)
          : null,
        index: currentSectionOverviewIndex + 2,
        eventType: ACTIVITY_BUILDER_EVENT_SECTION_VIEW,
      });
    }
  }, [
    currentSectionOverviewIndex,
    isEndOfLesson,
    sortedSectionsAndSubSectionsOverview,
    updateActiveSection,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLInputElement;

      if (
        target.type !== 'radio' &&
        target.type !== 'textarea' &&
        !target.classList.contains('ProseMirror')
      ) {
        if (e.key === ARROW_LEFT) {
          handlePrevious();
        } else if (e.key === ARROW_RIGHT) {
          handleNext();
        }
      }
    },
    [handleNext, handlePrevious]
  );

  return {
    handlePrevious,
    handleNext,
    handleKeyDown,
  };
};

export const useMountPresentationSession = () => {
  useEffect(() => {
    setPresentationSessionId();
    pushDataTrackerPageView();

    window.addEventListener('beforeunload', clearPresentationSessionId);

    return () => {
      // clear check answer data and presentation session id when leaving present mode
      checkAnswerVar({});
      window.removeEventListener('beforeunload', clearPresentationSessionId);
      clearPresentationSessionId();
    };
  }, []);
};

export const usePresentTabNavigation = (id: string) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const el = document.getElementById(id);
      // allow the left and right arrow keys to navigate between tabs
      // but prevent them moving to the prev/next section when the tab list is not focused
      if (el && !el.contains(document.activeElement)) {
        if (e.key === ARROW_LEFT) {
          e.preventDefault();
          e.stopPropagation();
        } else if (e.key === ARROW_RIGHT) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    },
    [id]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleKeyDown]);
};
